<template>
  <div class="set-builder-page">
    <div class="page-header">
      <div>
        <h1>Structure Set Builder</h1>
        <h2 v-if="structureSet">{{ structureSet.name }}</h2>
      </div>
      <router-link class="back-link" to="/project-structure">Back to Structure Management</router-link>
    </div>

    <section class="rules-panel">
      <div class="panel-header">
        <h2>Allowed Parent/Child Rules<HelpLink topic="customising-structure" section="adding-rules" title="How rules work" /></h2>
        <div class="panel-actions">
          <span v-if="loadingRules" class="muted">Loading...</span>
          <button class="create-button" type="button" @click="openCreateRuleModal">Add Rule</button>
        </div>
      </div>

      <div class="rules-layout">
        <div class="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Parent Type</th>
                <th>Child Type</th>
                <th class="actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="rule in sortedRules" :key="`rule-${getRuleId(rule)}`">
                <td>{{ parentLabel(rule) }}</td>
                <td>{{ typeNameById(getChildTypeId(rule)) }}</td>
                <td class="actions-col">
                  <button class="small-button" type="button" @click="openEditRuleModal(rule)">Edit</button>
                  <button class="small-button remove-button" type="button" @click="deleteRule(rule)">Delete</button>
                </td>
              </tr>
              <tr v-if="!loadingRules && !rules.length">
                <td colspan="3" class="empty-row">No rules found for this structure set.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="node-map-wrapper">
          <h3>Node Map</h3>

          <div v-if="!loadingRules && !rules.length" class="empty-row">
            No rules found for this structure set.
          </div>

          <div v-else-if="nodeMapRootIds.length" class="node-map-roots">
            <StructureRuleTreeNode
              v-for="rootId in nodeMapRootIds"
              :key="`root-${rootId}`"
              :node-id="rootId"
              :type-map="typeMap"
              :children-by-parent="childrenByParent"
              :path-ids="[]"
            />
          </div>

          <div v-else class="empty-row">
            Could not determine a root type for this rule set.
          </div>
        </div>
      </div>
    </section>

    <div
      v-if="showRuleModal"
      class="modal-overlay"
      role="dialog"
      aria-modal="true"
      :aria-label="editingRuleId ? 'Edit structure rule' : 'Create structure rule'"
      @click.self="closeRuleModal"
    >
      <div class="modal-content">
        <div class="modal-header">
          <h4>{{ editingRuleId ? 'Edit Rule' : 'Create Rule' }}</h4>
          <button class="modal-close" type="button" aria-label="Close" @click="closeRuleModal">×</button>
        </div>

        <form class="modal-form" @submit.prevent="submitRule">
          <div class="form-group">
            <label for="rule-parent-type">Parent Type</label>
            <select id="rule-parent-type" v-model="ruleForm.parent_type_id" class="form-control" required>
              <option :value="PROJECT_PARENT_VALUE">Project</option>
              <option v-for="type in structureTypes" :key="`parent-${type.id}`" :value="type.id">
                {{ type.name }}
              </option>
            </select>
          </div>

          <div class="form-group">
            <label for="rule-child-type">Child Type</label>
            <select id="rule-child-type" v-model.number="ruleForm.child_type_id" class="form-control" required>
              <option :value="null">Select child type</option>
              <option v-for="type in availableChildTypes" :key="`child-${type.id}`" :value="type.id">
                {{ type.name }}
              </option>
            </select>
          </div>

          <div class="modal-actions">
            <button class="secondary-button" type="button" @click="closeRuleModal">Cancel</button>
            <button class="submit-button" type="submit" :disabled="submittingRule">
              {{ submittingRule ? 'Saving...' : editingRuleId ? 'Save Changes' : 'Create Rule' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { useToast } from 'vue-toastification';
import apiService from '@/services/apiService';
import StructureRuleTreeNode from '@/components/StructureRuleTreeNode.vue';

const defaultRuleForm = () => ({
  parent_type_id: 'PROJECT_ROOT',
  child_type_id: null,
});

const PROJECT_PARENT_VALUE = 'PROJECT_ROOT';

export default {
  name: 'ProjectStructureSetBuilderPage',
  components: {
    StructureRuleTreeNode,
  },
  props: {
    setId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      PROJECT_PARENT_VALUE,
      structureSet: null,
      structureTypes: [],
      rules: [],
      loadingRules: false,
      submittingRule: false,
      showRuleModal: false,
      editingRuleId: null,
      ruleForm: defaultRuleForm(),
    };
  },
  computed: {
    ...mapState(['loggedInUser']),

    typeMap() {
      const map = this.structureTypes.reduce((acc, item) => {
        acc[item.id] = item.name;
        return acc;
      }, {});
      map[PROJECT_PARENT_VALUE] = 'Project';
      map['EXPERIMENTS'] = 'Experiments';
      return map;
    },

    sortedRules() {
      return [...this.rules].sort((a, b) => {
        const aIsProject = this.getParentTypeId(a) == null ? 0 : 1;
        const bIsProject = this.getParentTypeId(b) == null ? 0 : 1;
        return aIsProject - bIsProject;
      });
    },

    childrenByParent() {
      const acc = this.rules.reduce((map, rule) => {
        const parentId = this.getParentTypeId(rule);
        const childId = this.getChildTypeId(rule);
        if (!childId) {
          return map;
        }

        const parentKey = parentId ?? PROJECT_PARENT_VALUE;

        if (!map[parentKey]) {
          map[parentKey] = [];
        }
        map[parentKey].push(childId);
        return map;
      }, {});

      // Types with is_experiment_parent get a virtual Experiments child node
      for (const type of this.structureTypes) {
        if (type.is_experiment_parent) {
          if (!acc[type.id]) {
            acc[type.id] = [];
          }
          if (!acc[type.id].includes('EXPERIMENTS')) {
            acc[type.id].push('EXPERIMENTS');
          }
        }
      }

      return acc;
    },

    usedChildTypeIds() {
      return new Set(
        this.rules
          .map((rule) => this.getChildTypeId(rule))
          .filter(Boolean)
      );
    },

    availableChildTypes() {
      return this.structureTypes.filter((type) => {
        if (!this.usedChildTypeIds.has(type.id)) {
          return true;
        }
        // Allow the type that is already assigned to the rule being edited
        return type.id === this.ruleForm.child_type_id;
      });
    },

    nodeMapRootIds() {
      const hasProjectAsParent = this.rules.some((rule) => this.getParentTypeId(rule) == null);
      if (hasProjectAsParent) {
        return [PROJECT_PARENT_VALUE];
      }

      // Infer roots: parent types that are never a child
      const parentIds = new Set();
      const childIds = new Set();

      for (const rule of this.rules) {
        const parentId = this.getParentTypeId(rule);
        const childId = this.getChildTypeId(rule);
        if (parentId) parentIds.add(parentId);
        if (childId) childIds.add(childId);
      }

      const inferredRoots = Array.from(parentIds).filter((id) => !childIds.has(id));
      return inferredRoots.length ? inferredRoots : Array.from(parentIds);
    },
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  created() {
    this.refreshData();
  },
  methods: {
    normalizeCollection(payload, key) {
      if (Array.isArray(payload)) {
        return payload;
      }

      if (payload && Array.isArray(payload.items)) {
        return payload.items;
      }

      if (payload && Array.isArray(payload[key])) {
        return payload[key];
      }

      if (payload && payload.data && Array.isArray(payload.data.items)) {
        return payload.data.items;
      }

      if (payload && payload.data && Array.isArray(payload.data)) {
        return payload.data;
      }

      return [];
    },

    normalizeEntity(payload, key) {
      if (!payload || Array.isArray(payload)) {
        return null;
      }

      if (payload[key] && typeof payload[key] === 'object') {
        return payload[key];
      }

      if (payload.item && typeof payload.item === 'object') {
        return payload.item;
      }

      if (payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data)) {
        if (payload.data[key] && typeof payload.data[key] === 'object') {
          return payload.data[key];
        }
        return payload.data;
      }

      return payload;
    },

    getRuleId(rule) {
      return rule?.id ?? rule?.rule_id ?? null;
    },

    getParentTypeId(rule) {
      return rule?.parent_type_id ?? rule?.parent_structure_type_id ?? rule?.parent_id ?? rule?.parent_type?.id ?? null;
    },

    getChildTypeId(rule) {
      return rule?.child_type_id ?? rule?.child_structure_type_id ?? rule?.child_id ?? rule?.child_type?.id ?? null;
    },

    parentLabel(rule) {
      const parentId = this.getParentTypeId(rule);
      if (parentId == null) {
        return 'Project';
      }
      return this.typeNameById(parentId);
    },

    typeNameById(typeId) {
      if (typeId === PROJECT_PARENT_VALUE) {
        return 'Project';
      }

      if (!typeId) {
        return '-';
      }
      return this.typeMap[typeId] || `Type #${typeId}`;
    },

    async refreshData() {
      await Promise.all([this.fetchStructureSet(), this.fetchStructureTypes(), this.fetchRules()]);
    },

    async fetchStructureSet() {
      try {
        const response = await apiService.getProjectStructureSet(this.setId, this.loggedInUser.entra_id);
        this.structureSet = this.normalizeEntity(response, 'set');
      } catch (error) {
        this.structureSet = null;
        this.toast.error('Failed to fetch structure set details.');
      }
    },

    async fetchStructureTypes() {
      try {
        const response = await apiService.getProjectStructureTypes(this.loggedInUser.entra_id);
        this.structureTypes = this.normalizeCollection(response, 'types');
      } catch (error) {
        this.structureTypes = [];
        this.toast.error('Failed to fetch structure types.');
      }
    },

    async fetchRules() {
      this.loadingRules = true;
      try {
        const response = await apiService.getProjectStructureSetRules(this.setId, this.loggedInUser.entra_id);
        this.rules = this.normalizeCollection(response, 'rules');
      } catch (error) {
        this.rules = [];
        this.toast.error('Failed to fetch structure set rules.');
      } finally {
        this.loadingRules = false;
      }
    },

    openCreateRuleModal() {
      this.editingRuleId = null;
      this.ruleForm = defaultRuleForm();
      this.showRuleModal = true;
    },

    openEditRuleModal(rule) {
      const ruleId = this.getRuleId(rule);
      if (!ruleId) {
        this.toast.error('Rule id is missing and cannot be edited.');
        return;
      }

      this.editingRuleId = ruleId;
      this.ruleForm = {
        parent_type_id: this.getParentTypeId(rule) ?? PROJECT_PARENT_VALUE,
        child_type_id: this.getChildTypeId(rule),
      };
      this.showRuleModal = true;
    },

    closeRuleModal() {
      if (this.submittingRule) {
        return;
      }
      this.showRuleModal = false;
    },

    async submitRule() {
      if (!this.ruleForm.child_type_id) {
        this.toast.error('Child type is required.');
        return;
      }

      const selectedParent = this.ruleForm.parent_type_id;
      const parentTypeId = selectedParent === PROJECT_PARENT_VALUE ? null : Number(selectedParent);

      if (selectedParent !== PROJECT_PARENT_VALUE && Number.isNaN(parentTypeId)) {
        this.toast.error('Parent type is invalid.');
        return;
      }

      const payload = {
        parent_type_id: parentTypeId,
        child_type_id: Number(this.ruleForm.child_type_id),
      };

      this.submittingRule = true;
      try {
        if (this.editingRuleId) {
          await apiService.updateProjectStructureSetRule(
            this.setId,
            this.editingRuleId,
            payload,
            this.loggedInUser.entra_id
          );
          this.toast.success('Rule updated.');
        } else {
          await apiService.createProjectStructureSetRule(this.setId, payload, this.loggedInUser.entra_id);
          this.toast.success('Rule created.');
        }

        this.showRuleModal = false;
        this.ruleForm = defaultRuleForm();
        this.editingRuleId = null;
        await this.fetchRules();
      } catch (error) {
        this.toast.error(error?.response?.data?.message || 'Failed to save rule.');
      } finally {
        this.submittingRule = false;
      }
    },

    async deleteRule(rule) {
      const ruleId = this.getRuleId(rule);
      if (!ruleId) {
        this.toast.error('Rule id is missing and cannot be deleted.');
        return;
      }

      const confirmed = window.confirm('Delete this rule?');
      if (!confirmed) {
        return;
      }

      try {
        await apiService.deleteProjectStructureSetRule(this.setId, ruleId, this.loggedInUser.entra_id);
        this.toast.success('Rule deleted.');
        await this.fetchRules();
      } catch (error) {
        this.toast.error(error?.response?.data?.message || 'Failed to delete rule.');
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/assets/main.scss';

.set-builder-page {
  max-width: 1200px;
  margin: 20px auto;
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 18px;

  h1 {
    margin: 0 0 8px;
    color: $primary-color;
  }

  p {
    margin: 0;
  }
}

.back-link {
  color: $primary-color;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.rules-panel {
  background: $secondary-color;
  border-radius: 8px;
  border: 1px solid rgba($primary-color, 0.18);
  padding: 16px;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;

  h2 {
    margin: 0;
    color: $primary-color;
  }
}

.panel-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.rules-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  align-items: start;
}

.muted {
  color: rgba($primary-color, 0.8);
}

.create-button {
  padding: 8px 12px;
  font-size: 0.9rem;
}

.table-wrapper {
  overflow-x: auto;

  table {
    width: 100%;
  }
}

.node-map-wrapper {
  overflow: auto;
  padding: 4px 8px;
  border: 1px solid rgba($primary-color, 0.2);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.65);

  h3 {
    margin: 0 0 10px;
    color: $primary-color;
  }
}

.node-map-roots {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
  min-width: max-content;
}

.actions-col {
  width: 180px;
  text-align: right;
}

.small-button {
  padding: 6px 10px;
  font-size: 0.85rem;
  margin-left: 8px;
}

.remove-button {
  background: #b00020;
}

.empty-row {
  text-align: center;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 2200;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
}

.modal-content {
  width: min(560px, 100%);
  background: white;
  border-radius: 10px;
  padding: 16px;
  border: 1px solid rgba($primary-color, 0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;

  h4 {
    margin: 0;
    color: $primary-color;
  }
}

.modal-close {
  background: transparent;
  color: $primary-color;
  border: 1px solid rgba($primary-color, 0.2);
  border-radius: 4px;
  width: 32px;
  height: 32px;
  padding: 0;
  font-size: 1.2rem;
  line-height: 1;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 10px;
}

.secondary-button {
  background: white;
  color: $primary-color;
  border: 1px solid rgba($primary-color, 0.4);
}

@media (max-width: 900px) {
  .rules-layout {
    grid-template-columns: 1fr;
  }

  .actions-col {
    width: 160px;
  }
}

@media (max-width: 700px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .actions-col {
    text-align: left;
  }

  .small-button {
    margin: 4px 8px 0 0;
  }
}
</style>
